"use client"

import * as React from "react";
import NextImage from "next/image";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

type AvatarImageProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  /** Rendered-size hint for the optimizer. Avatars are small; 96px covers the
   *  common 24–48px sizes at retina density. Pass a larger value for a big
   *  profile avatar if crispness matters. */
  sizes?: string;
};

/**
 * Avatar image, optimized via `next/image` (WebP + right-sized fetch instead of
 * the full-resolution upload — a user's 2 MB photo becomes a ~2 KB thumbnail).
 *
 * It renders as a `fill` overlay inside the relative, sized, `overflow-hidden`
 * Avatar root, so it paints on top of `AvatarFallback`: the fallback shows
 * through until the image loads, and again if it errors or `src` is empty —
 * preserving the prior fallback behavior, minus Radix's own status preloader
 * (which fetched the unoptimized original). Optimization is gated to built
 * deployments; `next dev` serves the raw src (see next.config `unoptimized`).
 */
const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, src, alt = "", sizes = "96px" }, ref) => {
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => {
      setFailed(false);
    }, [src]);
    // Empty/failed → render nothing so the fallback shows. `blob:` previews
    // can't be optimized by next/image, so skip them too.
    if (!src || failed || src.startsWith("blob:")) return null;
    return (
      <NextImage
        ref={ref}
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        className={cn("object-cover", className)}
        onError={() => setFailed(true)}
      />
    );
  }
);
AvatarImage.displayName = "AvatarImage";

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };